// React & Libraries
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { nanoid } from "nanoid";

// Utilities & Helpers
import getImageUrl from "../../../utils/getImageUrl";
import HandleErrorsListFunc from "../../global-functions/handleErrorsListFunc";

// Redux Actions - Products
import { addProductFunc } from "../../../store/custom-actions/products-actions/AddProductSlicer";

// Redux Actions - Attributes & Categories
import { getAttributesFunc } from "../../../store/custom-actions/attributes-actions/GetAttributesSlicer";
import { getCategoriesFunc } from "../../../store/custom-actions/categories-actions/GetCategoriesSlicer";

// Custom Feature Components
import AttributeManager from "./AttributeManager";
import VariantAttributeCard from "./VariantAttributeCard";
import ProductVariantsTable from "./ProductVariantsTable";
import MediaSelectorButton from "../../components/media/MediaSelectorButton";

// UI Components
import PageTitle from "../../layouts/PageTitle";
import FormField from "../../components/ui/FormField";
import TextAreaField from "../../components/ui/TextAreaField";
import CheckboxField from "../../components/ui/CheckboxField";
import SelectBox from "../../components/ui/SelectBox";
import SubmitButton from "../../components/ui/SubmitButton";
import NestedTreeSelector from "../../components/ui/NestedTreeSelector";

// Hooks - Product Attributes
import {
  useProductVariants,
  handleToggleAttribute,
  handleToggleAttributeValue,
  rebuildVariantsFromAttributes,
} from "../../../hooks/useProductAttributes";

function NewProductForm() {
  // Imports & Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Form Setup with React Hook Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
    control,
    getValues,
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      slug: "",
      sku: "",
      stock: 0,
      min_stock: 0,
      price: 0,
      discount: 0,
      discount_type: "fixed",
      active: true,
      track_stock: true,
    },
  });

  // Redux Selectors
  const { loading: submitLoading } = useSelector(
    (state) => state.addProductSlicer
  );
  const { loading: categoriesLoading, data: categoriesList } = useSelector(
    (state) => state.getCategoriesSlicer
  );
  const { data: attributesData, loading: attributesLoading } = useSelector(
    (state) => state.getAttributesSlicer
  );

  // Local State
  const [categoryId, setCategoryId] = useState("");
  const [productAttributes, setProductAttributes] = useState({});
  const [productImages, setProductImages] = useState([]);
  const [imagesListViewr, setImagesSliteViewr] = useState([]);

  const [typesObject, setTypesObject] = useState({});
  const [keyInput, setKeyInput] = useState("");
  const [valueInput, setValueInput] = useState("");

  const [skuCode, setSkuCode] = useState(""); // Auto SKU
  const { variantFields, append, remove, update } = useProductVariants({
    control,
    skuCode,
    setValue,
  });

  // Effect: Fetch Initial Data
  useEffect(() => {
    dispatch(getCategoriesFunc());
    dispatch(getAttributesFunc());
  }, [dispatch]);

  // Effect: Handle Form Errors
  useEffect(() => {
    HandleErrorsListFunc(errors);
  }, [errors]);

  // Effect: Auto Generate SKU
  useEffect(() => {
    const generatedSKU = `ITEM-${nanoid(6).toUpperCase()}`;
    setSkuCode(generatedSKU);
    setValue("sku", generatedSKU);
  }, []);

  // Effect: Auto Generate Slug
  const titleValue = watch("title");

  useEffect(() => {
    if (titleValue?.trim()) {
      const titleWithoutSpace = titleValue.trim().replace(/\s+/g, "-");
      const generatedSlug = `${titleWithoutSpace}-${nanoid(6).toUpperCase()}`;
      setValue("slug", generatedSlug);
    } else {
      setValue("slug", "");
    }
  }, [titleValue, setValue]);

  /**
   * Handlers
   */
  // Change Category
  const changeCategoryId = (id) => {
    setCategoryId(id);
  };

  // Toggle Attribute
  const onAttributeToggle = (attributeId, isChecked) => {
    setProductAttributes((prev) =>
      handleToggleAttribute(
        attributeId,
        isChecked,
        attributesData,
        prev,
        (attrs) =>
          rebuildVariantsFromAttributes({
            newAttributes: attrs,
            watch,
            variantFields,
            append,
            remove,
            keepExisting: false, // true في صفحة التعديل فقط
          })
      )
    );
  };
  // Toggle Attribute Value
  const onAttributeValueToggle = (attributeId, valueId, isChecked) => {
    setProductAttributes((prev) =>
      handleToggleAttributeValue(
        attributeId,
        valueId,
        isChecked,
        attributesData,
        prev,
        (attrs) =>
          rebuildVariantsFromAttributes({
            newAttributes: attrs,
            watch,
            variantFields,
            append,
            remove,
            keepExisting: false, // true في صفحة التعديل فقط
          })
      )
    );
  };

  // Form Submit Handler
  const onSubmit = async (data) => {
    const cleanedVariants =
      data.variants?.map(({ attribute_values_display, ...rest }) => rest) || [];

    const payload = {
      ...data,
      category_id: categoryId,
      product_attributes: typesObject,
      variants: cleanedVariants,
    };

    const result = await dispatch(addProductFunc({ data: payload }));

    if (addProductFunc.fulfilled.match(result)) {
      reset();
      navigate("/products");
    }
  };

  return (
    <>
      <PageTitle motherMenu={"Products"} activeMenu={"New Item"}  motherMenuPath="/products" />
      <form id="submit-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          {/*  Back & submit button */}
          <div className="text-end toolbar toolbar-bottom ">
            <div className="toolbar toolbar-bottom pb-3 d-flex justify-content-end">
              {/* Back Button */}
              <Link to="/products">
                <button
                  type="button"
                  className="btn btn-secondary sw-btn-back me-2"
                >
                  Back
                </button>
              </Link>
              <SubmitButton isLoading={submitLoading} form="submit-form" />
            </div>
          </div>
          <div className="col-xl-12 col-xxl-12">
            <div className="row">
              <div className="col-12 col-lg-8">
                <div className="card">
                  <h5 className="card-title p-4"> {t("Product details")}</h5>
                  <div className="card-body">
                    <>
                      <div>
                        <FormField
                          label="Title"
                          name="title"
                          register={register}
                          required
                          errors={errors}
                        />
                        <div className="form-group mb-3">
                          <label>{t("Category")}</label>
                          <NestedTreeSelector
                            treeData={categoriesList}
                            onSelect={changeCategoryId}
                            loading={categoriesLoading}
                          />
                        </div>
                        <FormField
                          label="SKU Code"
                          name="sku"
                          register={register}
                          required
                          errors={errors}
                        />
                        <FormField
                          label="Slug"
                          name="slug"
                          register={register}
                          required
                          errors={errors}
                        />
                        <FormField
                          label="Product Stock"
                          name="stock"
                          register={register}
                          required
                          errors={errors}
                        />
                        <FormField
                          label="Min Stock"
                          name="min_stock"
                          register={register}
                          required
                          errors={errors}
                        />
                        <TextAreaField
                          label="Description"
                          name="description"
                          isRichTextEditor
                          register={register}
                          required
                          errors={errors}
                          watch={watch}
                          setValue={setValue}
                        />
                      </div>
                    </>
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="row ">
                  {/* product status  */}
                  <div className="col-12">
                    <div className="card">
                      <h5 className="card-title  p-4">
                        {t("Product status")}
                      </h5>
                      <div className="card-body">
                        <>
                          <CheckboxField
                            name="active"
                            label="Activity"
                            register={register}
                            errors={errors}
                            defaultChecked
                          />
                          <CheckboxField
                            name="recomended"
                            label="Recomended"
                            register={register}
                            errors={errors}
                            defaultChecked
                          />
                          <CheckboxField
                            name="best_selling"
                            label="Best Selling"
                            register={register}
                            errors={errors}
                            defaultChecked
                          />
                          <CheckboxField
                            name="track_stock"
                            label="Track Stock"
                            register={register}
                            errors={errors}
                            defaultChecked
                          />
                        </>
                      </div>
                    </div>
                  </div>
                  {/* product price  */}
                  <div className="col-12">
                    <div className="card">
                      <h5 className="card-title  p-4">
                        {t("Product pricing")}
                      </h5>
                      <div className="card-body">
                        <>
                          <FormField
                            label="Price"
                            name="price"
                            register={register}
                            required
                            errors={errors}
                          />
                          <FormField
                            label="Discount"
                            name="discount"
                            register={register}
                            required
                            errors={errors}
                          />
                          <SelectBox
                            label="Discount type"
                            name="discount_type"
                            register={register}
                            required={true}
                            errors={errors}
                            showLabel={true}
                            placeholder={t("Select discount type")}
                            data={[
                              { id: "fixed", name: t("Fixed") },
                              { id: "percentage", name: t("Percentage") },
                            ]}
                          />
                        </>
                      </div>
                    </div>
                  </div>
                  {/* product media  */}
                  <div className="col-12">
                    <div className="card">
                      <h5 className="card-title p-4">{t("Product Media")}</h5>
                      <div className="card-body">
                        <MediaSelectorButton
                          inputName="thumbnail_img"
                          required
                          register={register}
                          errors={errors}
                          onChange={(value) => setValue("thumbnail_img", value)}
                        />
                        <MediaSelectorButton
                          label="Images"
                          inputName="images"
                          selectionType="multiple"
                          required
                          register={register}
                          errors={errors}
                          onChange={(ids, images) => {
                            setValue("images", ids);
                            setProductImages(ids);
                            setImagesSliteViewr(
                              images.map((img) => ({
                                value: img.id,
                                src: getImageUrl(img.path),
                              }))
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* product meta  */}
            <div className="col-12">
              <div className="card">
                <h5 className="card-title  p-4">{t("Meta product")}</h5>
                <div className="card-body">
                  <>
                    <TextAreaField
                      label="Meta description"
                      name="meta_description"
                      register={register}
                      errors={errors}
                      placeholder="Enter meta description"
                    />
                    <FormField
                      label="Meta keyword"
                      name="meta_keywords"
                      register={register}
                      errors={errors}
                    />
                  </>
                </div>
              </div>
            </div>
            <div className="col-12">
              {/* product attributes */}
              <div className="card">
                <h5 className="card-title  p-4">
                  {t("Product attributes")}
                </h5>
                <div className="card-body">
                  <>
                    <AttributeManager
                      typesObject={typesObject}
                      setTypesObject={setTypesObject}
                      keyInput={keyInput}
                      valueInput={valueInput}
                      setKeyInput={setKeyInput}
                      setValueInput={setValueInput}
                    />
                  </>
                </div>
              </div>
            </div>
            {/* Product Variant */}
            {!attributesLoading &&
              attributesData.map((attribute, index) => (
                <div className="col-12" key={index}>
                  <VariantAttributeCard
                    attribute={attribute}
                    selected={!!productAttributes[attribute.id]}
                    selectedValues={
                      productAttributes[attribute.id]?.values || []
                    }
                    onToggleAttribute={onAttributeToggle}
                    onToggleValue={onAttributeValueToggle}
                  />
                </div>
              ))}
            <div className="col-12">
              {Object.keys(productAttributes).length >= 1 && (
                <ProductVariantsTable
                  productAttributes={productAttributes}
                  variantFields={variantFields}
                  productImages={productImages}
                  imagesListViewr={imagesListViewr}
                  register={register}
                  getValues={getValues}
                  update={update}
                  watch={watch}
                  skuCode={skuCode}
                />
              )}
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default NewProductForm;
